import {
  getLeoUuid,
  LeoUUID,
  getList,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { QuartileConfiguration, QuartileConfigurationDTO } from "./quartileConfiguration";
import { QuartileColorWithHexCodes, QuartileColorWithHexCodesDTO } from "./quartileColorWithHexCodes";
/* eslint-disable import/export */
export abstract class GetSurveyDashboardPreferencesRPC implements
  LeoRPC<GetSurveyDashboardPreferencesRPC.Request, GetSurveyDashboardPreferencesRPC.Response, GetSurveyDashboardPreferencesRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveyDashboardPreferencesRPC.Request
  ): Promise<LeoRPCResult<GetSurveyDashboardPreferencesRPC.Response, GetSurveyDashboardPreferencesRPC.Errors.Errors>>;
}

export namespace GetSurveyDashboardPreferencesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveyDashboardPreferencesRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      return new Request(
        projectId,
        surveyId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveyDashboardPreferencesRPC(projectId='${this.projectId}',surveyId='${this.surveyId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    quartileConfigurations: QuartileConfigurationDTO[];
    quartileColors: QuartileColorWithHexCodesDTO[];
    isSurveyViewOptionDisplayTextSelected: boolean;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveyDashboardPreferencesResponse: boolean = true;
  
    readonly quartileConfigurations: QuartileConfiguration[];
    readonly quartileColors: QuartileColorWithHexCodes[];
    readonly isSurveyViewOptionDisplayTextSelected: boolean;
  
  
    constructor(
    quartileConfigurations: QuartileConfiguration[], 
    quartileColors: QuartileColorWithHexCodes[], 
    isSurveyViewOptionDisplayTextSelected: boolean
    ) {
      this.quartileConfigurations = quartileConfigurations;
      this.quartileColors = quartileColors;
      this.isSurveyViewOptionDisplayTextSelected = isSurveyViewOptionDisplayTextSelected;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const quartileConfigurations = getList<QuartileConfigurationDTO, QuartileConfiguration>(
        dto,
        "quartileConfigurations",
        QuartileConfiguration.fromDTO,
        InvalidResponseQuartileConfigurationsError
      );
      const quartileColors = getList<QuartileColorWithHexCodesDTO, QuartileColorWithHexCodes>(
        dto,
        "quartileColors",
        QuartileColorWithHexCodes.fromDTO,
        InvalidResponseQuartileColorsError
      );
      const isSurveyViewOptionDisplayTextSelected = getBoolean(dto, "isSurveyViewOptionDisplayTextSelected", InvalidResponseIsSurveyViewOptionDisplayTextSelectedError);
      return new Response(
        quartileConfigurations,
        quartileColors,
        isSurveyViewOptionDisplayTextSelected
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          quartileConfigurations: this.quartileConfigurations.map((e: QuartileConfiguration) => { return e.toDTO() }),
          quartileColors: this.quartileColors.map((e: QuartileColorWithHexCodes) => { return e.toDTO() }),
          isSurveyViewOptionDisplayTextSelected: this.isSurveyViewOptionDisplayTextSelected
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveyDashboardPreferences(quartileConfigurations='${this.quartileConfigurations}',quartileColors='${this.quartileColors}',isSurveyViewOptionDisplayTextSelected='${this.isSurveyViewOptionDisplayTextSelected}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetSurveyDashboardPreferencesRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveyDashboardPreferencesRPC.INVALID_SURVEY_ID()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetSurveyDashboardPreferencesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends GetSurveyDashboardPreferencesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidResponseQuartileConfigurationsError extends GetSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuartileConfigurationsError";
  }
}

export class InvalidResponseQuartileColorsError extends GetSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuartileColorsError";
  }
}

export class InvalidResponseIsSurveyViewOptionDisplayTextSelectedError extends GetSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsSurveyViewOptionDisplayTextSelectedError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

